import { GetStaticProps } from 'next';
import React, { useEffect } from 'react';

import RichText from 'components/RichText/RichText';
import SEO from 'components/SEO/SEO';
import { ModuleListProps } from 'containers/InsightPage/InsightPage.types';
import { PrivacyPageType } from 'containers/PrivacyPage/PrivacyPage.types';
import IndentTextSection from 'containers/PrivacyPage/sections/IndentTextSection/IndentTextSection';
import TableSection from 'containers/PrivacyPage/sections/TableSection/TableSection';
import TextBlockSection from 'containers/PrivacyPage/sections/TextBlockSection/TextBlockSection';
import { useCopyStore } from 'store';
import { CopyStoreType } from 'store/copy.types';
import { queryCMS } from 'utils/cms/api';
import { privacyPage } from 'utils/cms/gql';
import { ISR_TIMEOUT } from 'utils/config';
import { marketPageId, MarketPages } from 'utils/sharedStrapiQueries/market';
import { pageMotionProps } from 'utils/styles/animations';

import * as S from './PrivacyPage.styles';

export interface PrivacyPageProps {
  initialCopy: {
    head: CopyStoreType['copy']['head'];
    global: CopyStoreType['copy']['global'];
    globalSettings: CopyStoreType['copy']['globalSettings'];
    redirectPopup: CopyStoreType['copy']['redirectPopup'];
    cookieBar: CopyStoreType['copy']['cookieBar'];
    navbar: CopyStoreType['copy']['navbar'];
    footer: CopyStoreType['copy']['footer'];
  };
  page: PrivacyPageType;
}

const defaultProps: Partial<PrivacyPageProps> = {};

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const id = await marketPageId(MarketPages.accessibilityPage, locale);
  const cookieId = await marketPageId(MarketPages.cookieBar, locale);
  const footerId = await marketPageId(MarketPages.footer, locale);

  const globalIds = { cookieId, footerId };

  const response = await queryCMS({
    query: privacyPage({ id, locale, globalIds }),
  });

  const {
    data: {
      sharedCopies,
      privacyPage: page,
      redirectPopups,
      cookieBar,
      navbar,
      footer,
    },
  } = response;

  if (!page || !sharedCopies) return { notFound: true };

  return {
    props: {
      initialCopy: {
        head: page?.seo || null,
        global: sharedCopies[0]?.global || null,
        globalSettings: sharedCopies[0] || null,
        redirectPopup: redirectPopups[0] || null,
        cookieBar: cookieBar || null,
        navbar,
        footer,
      },
      page,
    },
    revalidate: ISR_TIMEOUT,
  };
};

const PrivacyPage: React.FunctionComponent<PrivacyPageProps> = ({
  initialCopy,
  page,
}) => {
  const { setCopy } = useCopyStore();

  useEffect(() => {
    setCopy(initialCopy);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCopy]);

  const modulesList: ModuleListProps = {
    ComponentSectionsIndentedTextSection: IndentTextSection,
    ComponentSectionsPrivacyTextBlockSection: TextBlockSection,
    ComponentSectionsTableSection: TableSection,
  };

  const renderModules = () =>
    page.modules.map((module, idx) => {
      const moduleName = module.__typename;
      const ModuleComponent = modulesList[moduleName];

      return (
        ModuleComponent && (
          <ModuleComponent key={`${module.__typename}${idx}`} data={module} />
        )
      );
    });

  return (
    <S.Wrapper {...pageMotionProps}>
      {page?.seo && <SEO seo={page.seo} />}
      <S.HeadingWrapper>
        <S.Title>{page.title}</S.Title>
        <RichText richText={page.description} />
      </S.HeadingWrapper>
      {renderModules()}
    </S.Wrapper>
  );
};

PrivacyPage.defaultProps = defaultProps;

export default PrivacyPage;
